import { ReactNode } from 'react';

import { Stack, Typography } from '@mui/material';
// eslint-disable-next-line no-restricted-imports
import { type ConfirmOptions, useConfirm } from 'material-ui-confirm';
import { Trans, useTranslation } from 'react-i18next';

import { Icon } from '@/components/ui/Icon';
import { uiIcons } from '@/constants/uiIcons';

export type ConfirmDialogVariant = 'delete' | 'save' | 'default';

export interface UseConfirmDialogOptions extends Omit<
  ConfirmOptions,
  | 'title'
  | 'description'
  | 'cancellationText'
  | 'confirmationText'
  | 'cancellationButtonProps'
> {
  variant?: ConfirmDialogVariant;
  title?: string;
  description?: ReactNode;
  cancellationText?: string;
  confirmationText?: string;
  hideWarningIcon?: boolean;
  hideDescription?: boolean;
}

export const useConfirmDialog = () => {
  const confirm = useConfirm();
  const { t } = useTranslation('ui');

  const getVariantDefaults = (variant: ConfirmDialogVariant) => {
    const fixedDefaults = {
      confirmationButtonProps: {
        color: 'secondary' as const,
        variant: 'contained' as const,
        autoFocus: true,
      },
      cancellationButtonProps: {
        variant: 'text' as const,
        color: 'secondary' as const,
      },
      cancellationText: t('confirmationModal.CancelLabel'),
    };
    switch (variant) {
      case 'delete':
        return {
          ...fixedDefaults,
          title: t('confirmationModal.delete.Title'),
          description: (
            <Trans
              i18nKey="confirmationModal.delete.Description"
              ns="ui"
              key="deleteButton.DefaultConfirmDescription"
              defaults={t('confirmationModal.delete.Description')}
              components={[
                <Typography key={0} variant="bodyBold" component="span" />,
                <Typography key={1} variant="bodyBold" component="span" />,
              ]}
            />
          ),
          confirmationText: t('confirmationModal.delete.ConfirmLabel'),
        };
      case 'save':
        return {
          ...fixedDefaults,
          title: t('confirmationModal.save.Title'),
          description: t('confirmationModal.save.Description'),
          confirmationText: t('confirmationModal.save.ConfirmLabel'),
        };
      case 'default':
      default:
        return {
          ...fixedDefaults,
          title: t('confirmationModal.default.Title'),
          description: t('confirmationModal.default.Description'),
          confirmationText: t('confirmationModal.default.ConfirmLabel'),
        };
    }
  };

  const showConfirm = async (options: UseConfirmDialogOptions = {}) => {
    const { variant = 'default', ...customOptions } = options;
    const variantDefaults = getVariantDefaults(variant);

    const mergedOptions: ConfirmOptions = {
      ...variantDefaults,
      ...customOptions,
      title: (
        <Stack direction="row" alignItems="center" gap={1} component="span">
          {variant === 'delete' && !options.hideWarningIcon && (
            <Icon name={uiIcons.status.warning} color="warning" />
          )}
          <span>{customOptions.title ?? variantDefaults.title}</span>
        </Stack>
      ),
      description: options.hideDescription
        ? undefined
        : (customOptions.description ?? variantDefaults.description),
      cancellationText:
        customOptions.cancellationText ?? variantDefaults.cancellationText,
      confirmationText:
        customOptions.confirmationText ?? variantDefaults.confirmationText,
      confirmationButtonProps: {
        ...variantDefaults.confirmationButtonProps,
        ...customOptions.confirmationButtonProps,
      },
    };

    return await confirm(mergedOptions);
  };

  return { showConfirm };
};
